import _ from 'lodash'
import { baseApi } from './base'
import { Schedule, ScheduleAvailability, ScheduleException, ScheduleRecurrenceType, Slot } from '../models/schedule.model'

export type CreateScheduleParams = {
  consultationReason: string
  createdBy?: string
  duration: number
  startDate: string
  endDate?: string
  startTime: number
  endTime: number
  recurrenceRule?: CreateScheduleRecurrenceRuleParams
}

type CreateScheduleExceptionParams = Pick<CreateScheduleParams, 'duration' | 'startDate' | 'endDate' | 'startTime' | 'endTime'> & {
  isRescheduled?: boolean
  isCancelled?: boolean
  recurrenceRule?: CreateScheduleRecurrenceRuleParams
  scheduleId: string
}

export type CreateScheduleRecurrenceRuleParams = {
  daysOfWeek: number[]
  datesOfMonth: number[]
  monthsOfYear: number[]
  recurrenceType: ScheduleRecurrenceType
  separationCount: number
}

export type GetSchedulesParams = { createdBy?: string[]; scheduleIds?: string[] }

export type GetScheduleAvailabilitiesParams = { owners: string[]; dateFrom: string; dateTo: string }

export type GetScheduleBusySlotsParams = { owners: string[]; dateFrom?: string; dateTo?: string }

export const scheduleApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createSchedule: builder.mutation<Schedule, CreateScheduleParams>({
      query: (body) => ({ url: '/schedules', method: 'POST', body }),
      async onQueryStarted(_createScheduleParams, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: createdSchedule } = await queryFulfilled
          const allGetSchedulesArgs = scheduleApi.util.selectCachedArgsForQuery(getState(), 'getSchedules')

          for (const args of allGetSchedulesArgs) {
            if (args.scheduleIds) continue
            if (args.createdBy && !args.createdBy.includes(createdSchedule.createdBy._id)) continue

            dispatch(
              scheduleApi.util.updateQueryData('getSchedules', args, (cachedSchedules) => {
                cachedSchedules.push(createdSchedule)
              })
            )
          }
        } catch {}
      },
    }),
    createScheduleException: builder.mutation<ScheduleException, CreateScheduleExceptionParams>({
      query: ({ scheduleId, ...body }) => ({ url: `/schedules/${scheduleId}/exceptions`, method: 'POST', body }),
      async onQueryStarted({ scheduleId }, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: scheduleException } = await queryFulfilled
          const allGetSchedulesArgs = scheduleApi.util.selectCachedArgsForQuery(getState(), 'getSchedules')

          for (const args of allGetSchedulesArgs) {
            dispatch(
              scheduleApi.util.updateQueryData('getSchedules', args, (cachedSchedules) => {
                const schedule = cachedSchedules.find((s) => s._id === scheduleId)
                if (schedule) {
                  schedule.exceptions.push(scheduleException)
                }
              })
            )
          }
        } catch {}
      },
    }),
    getSchedules: builder.query<Schedule[], GetSchedulesParams>({
      query: (params) => ({ url: '/schedules', method: 'GET', params }),
    }),
    deleteSchedule: builder.mutation<string, string>({
      query: (scheduleId) => ({ url: `/schedules/${scheduleId}`, method: 'DELETE' }),
      async onQueryStarted(scheduleId, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled
          const allGetSchedulesArgs = scheduleApi.util.selectCachedArgsForQuery(getState(), 'getSchedules')

          for (const args of allGetSchedulesArgs) {
            dispatch(
              scheduleApi.util.updateQueryData('getSchedules', args, (cachedSchedules) => {
                return cachedSchedules.filter((schedule) => schedule._id !== scheduleId)
              })
            )
          }
        } catch {}
      },
    }),
    getScheduleAvailabilities: builder.query<Record<string, ScheduleAvailability>, GetScheduleAvailabilitiesParams>({
      query: (params) => ({ url: '/schedules/availabilities', method: 'GET', params }),
    }),
    getBusySlots: builder.query<Slot[], GetScheduleBusySlotsParams>({
      query: (params) => ({ url: '/schedules/busy-slots', method: 'GET', params }),
    }),
  }),
  overrideExisting: false,
})
